import { TFunction } from "i18next";
import { isValidPhoneNumber } from "libphonenumber-js";
import validator from "validator";
import { z } from "zod";
import { AccountType } from "../../../account";

export const getUpdateAccountFormSchema = (t: TFunction) =>
  z
    .object({
      name: z.string().min(1, { message: t("admin.accounts.errors.name-required") }),
      type: z.nativeEnum(AccountType, {
        errorMap: () => ({
          message: t("admin.accounts.errors.type-required"),
        }),
      }),
      entityId: z
        .number()
        .positive()
        .nullable()
        .optional(),
      email: z
        .string()
        .email({ message: t("admin.accounts.errors.invalid-email") }),
      phoneNumber: z
        .string()
        .nullable()
        .transform(val => val === "" ? null : val)
        .refine(val => val === null || isValidPhoneNumber(val, "PT"), { message: t("admin.accounts.errors.invalid-phone") }),
      fiscalNumber: z
        .string()
        .nullable()
        .transform(val => val === "" ? null : val)
        .refine(val => val === null || validator.isTaxID(val, "pt-PT"), { message: t("admin.accounts.errors.invalid-fiscal-number") }),
      address: z
        .string()
        .nullable()
        .transform(val => val === "" ? null : val),
      zipCode: z
        .string()
        .nullable()
        .transform(val => val === "" ? null : val)
        .refine(val => val === null || validator.isPostalCode(val, "PT"), { message: t("admin.accounts.errors.invalid-zipcode") }),
      city: z
        .string()
        .nullable()
        .transform(val => val === "" ? null : val),
      description: z
        .string()
        .max(10240)
        .nullable()
        .transform(val => val === "" ? null : val),
    })
    .superRefine((values, ctx) => {
      const requiresEntity =
        values.type === AccountType.ENTITY || values.type === AccountType.ENTITY_WRITE;

      if (requiresEntity && !values.entityId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("admin.accounts.errors.entity-required"),
          path: ["entityId"],
        });
      }
    });

export type UpdateAccountForm = z.infer<ReturnType<typeof getUpdateAccountFormSchema>>;